import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { App as CapacitorApp, type BackButtonListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { StatusBar, Style } from '@capacitor/status-bar';
import { useLocation, useNavigate } from 'react-router-dom';
import { isNativeAndroid } from '@/lib/nativeRuntime';
import { queryKeys } from '@/hooks/queries';

/**
 * Recharge la liste des appareils au retour au premier plan : appStateChange
 * côté Android natif, visibilitychange côté web/PWA. Invalide uniquement le
 * cache React Query local (relecture getDeviceList) : ne déclenche jamais de
 * refreshLocation Vernal automatique.
 */
function useForegroundRefresh(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const refresh = (): void => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.devices });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    };

    if (isNativeAndroid()) {
      let disposed = false;
      let removeListener: (() => Promise<void>) | undefined;
      void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) refresh();
      }).then((handle) => {
        if (disposed) void handle.remove();
        else removeListener = handle.remove;
      });
      return () => {
        disposed = true;
        if (removeListener) void removeListener();
      };
    }

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [queryClient]);
}

function closeOpenOverlay(): boolean {
  const overlay = document.querySelector<HTMLElement>('dialog[open], [role="dialog"][aria-modal="true"]');
  if (!overlay) return false;
  overlay.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true }));
  return true;
}

export function NativeRuntime(): null {
  const location = useLocation();
  const navigate = useNavigate();

  useForegroundRefresh();

  useEffect(() => {
    if (!isNativeAndroid()) return;

    document.documentElement.dataset.nativePlatform = 'android';
    void StatusBar.setStyle({ style: Style.Dark });
    void StatusBar.setOverlaysWebView({ overlay: false });
    void StatusBar.setBackgroundColor({ color: '#f8fafc' });

    let disposed = false;
    let removeBackListener: (() => Promise<void>) | undefined;

    const onBack = (_event: BackButtonListenerEvent): void => {
      if (closeOpenOverlay()) return;
      if (location.pathname !== '/' && location.pathname !== '/login') {
        navigate(-1);
        return;
      }
      void CapacitorApp.exitApp();
    };

    void CapacitorApp.addListener('backButton', onBack).then((handle) => {
      if (disposed) void handle.remove();
      else removeBackListener = handle.remove;
    });

    const onExternalLink = (event: MouseEvent): void => {
      if (event.defaultPrevented || event.button !== 0) return;
      const anchor = (event.target as Element | null)?.closest<HTMLAnchorElement>('a[href]');
      if (!anchor) return;
      const target = new URL(anchor.href, window.location.href);
      if (!['http:', 'https:'].includes(target.protocol) || target.origin === window.location.origin) return;
      event.preventDefault();
      void Browser.open({ url: target.href });
    };
    document.addEventListener('click', onExternalLink, true);

    return () => {
      disposed = true;
      delete document.documentElement.dataset.nativePlatform;
      document.removeEventListener('click', onExternalLink, true);
      if (removeBackListener) void removeBackListener();
    };
  }, [location.pathname, navigate]);

  return null;
}
